import React, { useState, useEffect, useCallback } from 'react';
import { authApi, licensesApi, setTokens, clearTokens, getStoredUser, storeUser } from './api';

// ─── Theme & Helpers ────────────────────────────────────────────────────────
const T = {
  bg: '#060810', card: '#0c0f18', el: '#141924', hov: '#1a2030',
  brd: '#1c2438', text: '#e4e8f0', muted: '#7e8da6', dim: '#4a5670',
  pri: '#5b6cf0', priD: 'rgba(91,108,240,.12)',
  ok: '#10b981', okD: 'rgba(16,185,129,.12)',
  warn: '#f59e0b', warnD: 'rgba(245,158,11,.12)',
  err: '#ef4444', errD: 'rgba(239,68,68,.12)',
  cyan: '#06b6d4', cyanD: 'rgba(6,182,212,.12)',
  mag: '#c026d3', magD: 'rgba(192,38,211,.12)',
};

const Badge = ({ children, c = 'pri' }) => {
  const m = { pri: [T.pri, T.priD], ok: [T.ok, T.okD], warn: [T.warn, T.warnD], err: [T.err, T.errD], cyan: [T.cyan, T.cyanD], mag: [T.mag, T.magD] };
  const [fg, bg] = m[c] || m.pri;
  return <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: fg, background: bg, textTransform: 'uppercase', letterSpacing: .4 }}>{children}</span>;
};

const Btn = ({ children, v = 'def', onClick, full, disabled, s = {} }) => {
  const vs = { def: { background: T.el, border: `1px solid ${T.brd}`, color: T.text }, pri: { background: T.pri, border: 'none', color: '#fff' }, err: { background: T.errD, border: `1px solid rgba(239,68,68,.3)`, color: T.err } };
  return <button disabled={disabled} onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .5 : 1, width: full ? '100%' : 'auto', ...vs[v], ...s }}>{children}</button>;
};

const Input = ({ label, value, onChange, type = 'text', placeholder, options }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: T.muted, marginBottom: 5 }}>{label}</label>}
    {options ? <select value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${T.brd}`, background: T.el, color: T.text, fontSize: 13, outline: 'none' }}>{options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}</select>
    : <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${T.brd}`, background: T.el, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />}
  </div>
);

const Stat = ({ label, value, color }) => (
  <div style={{ background: T.card, borderRadius: 12, padding: '18px 22px', border: `1px solid ${T.brd}`, flex: 1, minWidth: 150 }}>
    <div style={{ fontSize: 11, color: T.muted, fontWeight: 500, marginBottom: 5, textTransform: 'uppercase', letterSpacing: .3 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: color || T.text, letterSpacing: -.5 }}>{value}</div>
  </div>
);

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(4px)' }} />
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', background: T.card, borderRadius: 16, border: `1px solid ${T.brd}`, width: 520, maxWidth: '92vw', maxHeight: '85vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ─── Login Screen ───────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await authApi.login(email, pass);
      if (data.user.role !== 'super_admin') { setError('Access denied. Super Admin only.'); setLoading(false); return; }
      setTokens(data.accessToken, data.refreshToken);
      storeUser(data.user);
      onLogin(data.user);
    } catch (err) { setError(err.response?.data?.error || 'Login failed'); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg, fontFamily: "'Outfit', sans-serif" }}>
      <div style={{ width: 400, padding: 36, background: T.card, borderRadius: 20, border: `1px solid ${T.brd}` }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `linear-gradient(135deg, ${T.pri}, ${T.mag})`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 24, fontWeight: 800, color: '#fff' }}>P</span>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>License Portal</h1>
          <p style={{ fontSize: 13, color: T.muted }}>Phoenix Suite Master Control</p>
        </div>
        <Input label="Email" value={email} onChange={setEmail} placeholder="admin@phoenixsuite.com.au" />
        <Input label="Password" value={pass} onChange={setPass} type="password" placeholder="Password" />
        {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: T.errD, color: T.err, fontSize: 12, marginBottom: 14 }}>{error}</div>}
        <Btn v="pri" full onClick={handleLogin} disabled={loading} s={{ marginBottom: 16, padding: '11px' }}>{loading ? 'Signing in...' : 'Sign In'}</Btn>
      </div>
    </div>
  );
};

// ─── Dashboard ──────────────────────────────────────────────────────────────
const DashboardView = ({ stats, orgs, onSelectOrg, onNewPartnerOrg }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>License Dashboard</h2>
        <p style={{ color: T.muted, fontSize: 13 }}>Manage all client organizations and module licenses</p>
      </div>
      <Btn v="pri" onClick={onNewPartnerOrg}>+ New partner org</Btn>
    </div>
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
      <Stat label="Total Organizations" value={stats.total_orgs || 0} />
      <Stat label="Active Licenses" value={stats.active_licenses || 0} color={T.ok} />
      <Stat label="Trial" value={stats.trial_licenses || 0} color={T.warn} />
      <Stat label="Monthly Revenue" value={`$${parseFloat(stats.total_mrr || 0).toLocaleString()}`} color={T.pri} />
    </div>
    <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
      <div style={{ flex: 1, background: T.card, borderRadius: 12, padding: 18, border: `1px solid ${T.brd}` }}>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>Module Breakdown</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1, background: T.priD, borderRadius: 8, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.pri }}>{stats.base_licenses || 0}</div>
            <div style={{ fontSize: 11, color: T.muted }}>Base</div>
          </div>
          <div style={{ flex: 1, background: T.magD, borderRadius: 8, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.mag }}>{stats.ma_licenses || 0}</div>
            <div style={{ fontSize: 11, color: T.muted }}>Martial Arts</div>
          </div>
          <div style={{ flex: 1, background: T.cyanD, borderRadius: 8, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.cyan }}>{stats.fit_licenses || 0}</div>
            <div style={{ fontSize: 11, color: T.muted }}>Fitness</div>
          </div>
        </div>
      </div>
    </div>
    {/* Organizations Table */}
    <div style={{ background: T.card, borderRadius: 12, border: `1px solid ${T.brd}`, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.brd}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Organizations ({orgs.length})</h3>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ background: T.el }}>{['Organization', 'Modules', 'Students', 'Staff', 'MRR', 'Status'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: T.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: .4, borderBottom: `1px solid ${T.brd}` }}>{h}</th>)}</tr></thead>
        <tbody>
          {orgs.map(org => {
            const lics = org.licenses || [];
            const mrr = lics.filter(l => l.status === 'active').reduce((a, l) => a + parseFloat(l.price_monthly || 0), 0);
            const hasBase = lics.some(l => l.module === 'base' && ['active', 'trial'].includes(l.status));
            const hasMA = lics.some(l => l.module === 'martial_arts' && ['active', 'trial'].includes(l.status));
            const hasFit = lics.some(l => l.module === 'fitness' && ['active', 'trial'].includes(l.status));
            const hasMkt = lics.some(l => l.module === 'marketing' && ['active', 'trial'].includes(l.status));
            const hasWeb = lics.some(l => l.module === 'website' && ['active', 'trial'].includes(l.status));
            return (
              <tr key={org.id} onClick={() => onSelectOrg(org)} style={{ cursor: 'pointer', borderBottom: `1px solid ${T.brd}` }} onMouseEnter={e => e.currentTarget.style.background = T.hov} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '12px 14px' }}><div style={{ fontWeight: 500 }}>{org.name}</div><div style={{ fontSize: 11, color: T.muted }}>{org.email}</div></td>
                <td style={{ padding: '12px 14px' }}><div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{hasBase && <Badge c="pri">Base</Badge>}{hasMA && <Badge c="mag">MA</Badge>}{hasFit && <Badge c="cyan">PT</Badge>}{hasMkt && <Badge c="warn">Mkt</Badge>}{hasWeb && <Badge c="ok">Web</Badge>}</div></td>
                <td style={{ padding: '12px 14px' }}>{org.student_count}</td>
                <td style={{ padding: '12px 14px' }}>{org.staff_count}</td>
                <td style={{ padding: '12px 14px', fontWeight: 600, color: T.ok }}>${mrr.toFixed(0)}/mo</td>
                <td style={{ padding: '12px 14px' }}><Badge c={hasBase ? 'ok' : 'err'}>{hasBase ? 'Active' : 'No License'}</Badge></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {orgs.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: T.dim }}>No organizations yet</div>}
    </div>
  </div>
);

// ─── Org Detail ─────────────────────────────────────────────────────────────
const OrgDetailView = ({ org, onBack, onRefresh }) => {
  const [issueModal, setIssueModal] = useState(false);
  const [issueForm, setIssueForm] = useState({ module: 'martial_arts', maxStudents: 50, maxStaff: 5, priceMonthly: 49 });
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});

  const handleIssue = async () => {
    try {
      await licensesApi.issue({ orgId: org.id, ...issueForm });
      setIssueModal(false);
      onRefresh();
    } catch (err) { alert(err.response?.data?.error || 'Failed to issue license'); }
  };

  const handleUpdate = async () => {
    try {
      await licensesApi.update(editModal.id, editForm);
      setEditModal(null);
      onRefresh();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update'); }
  };

  const handleRevoke = async (licId) => {
    if (!window.confirm('Revoke this license? The organization will lose access to this module immediately.')) return;
    try { await licensesApi.revoke(licId); onRefresh(); } catch (err) { alert('Failed to revoke'); }
  };

  const lics = org.licenses || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer' }} onClick={onBack}>
        <span style={{ color: T.muted, fontSize: 13 }}>← Back to Organizations</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>{org.name}</h2>
          <p style={{ color: T.muted, fontSize: 13 }}>{org.email} · {org.phone || 'No phone'} · {org.city || ''} {org.state || ''}</p>
        </div>
        <Btn v="pri" onClick={() => setIssueModal(true)}>Issue New License</Btn>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <Stat label="Students" value={org.student_count} />
        <Stat label="Staff" value={org.staff_count} />
        <Stat label="Licenses" value={lics.length} />
        <Stat label="MRR" value={`$${lics.filter(l => l.status === 'active').reduce((a, l) => a + parseFloat(l.price_monthly || 0), 0).toFixed(0)}`} color={T.ok} />
      </div>

      {/* Licenses */}
      <div style={{ background: T.card, borderRadius: 12, padding: 20, border: `1px solid ${T.brd}`, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 16px' }}>Module Licenses</h3>
        {lics.length === 0 ? <p style={{ color: T.dim, fontSize: 13 }}>No licenses issued</p> :
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {lics.map(lic => (
              <div key={lic.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, background: T.el, border: `1px solid ${T.brd}` }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <Badge c={lic.module === 'base' ? 'pri' : lic.module === 'martial_arts' ? 'mag' : lic.module === 'fitness' ? 'cyan' : lic.module === 'marketing' ? 'warn' : 'ok'}>
                      {{ base: 'Base', martial_arts: 'Martial Arts', fitness: 'Fitness', marketing: 'Marketing', website: 'Website' }[lic.module] || lic.module}
                    </Badge>
                    <Badge c={lic.status === 'active' ? 'ok' : lic.status === 'trial' ? 'warn' : lic.status === 'suspended' ? 'err' : 'err'}>{lic.status}</Badge>
                  </div>
                  <div style={{ fontSize: 12, color: T.muted }}>
                    Key: <code style={{ background: T.bg, padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>{lic.license_key}</code>
                    {' · '}Max: {lic.max_students} students, {lic.max_staff} staff
                    {lic.price_monthly && ` · $${parseFloat(lic.price_monthly).toFixed(0)}/mo`}
                    {lic.expires_at && ` · Expires: ${new Date(lic.expires_at).toLocaleDateString()}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn s={{ padding: '5px 12px', fontSize: 12 }} onClick={() => { setEditModal(lic); setEditForm({ status: lic.status, maxStudents: lic.max_students, maxStaff: lic.max_staff, priceMonthly: parseFloat(lic.price_monthly || 0) }); }}>Edit</Btn>
                  <Btn v="err" s={{ padding: '5px 12px', fontSize: 12 }} onClick={() => handleRevoke(lic.id)}>Revoke</Btn>
                </div>
              </div>
            ))}
          </div>
        }
      </div>

      {/* Audit Log */}
      {org.auditLog && org.auditLog.length > 0 && (
        <div style={{ background: T.card, borderRadius: 12, padding: 20, border: `1px solid ${T.brd}` }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 16px' }}>Audit Log</h3>
          {org.auditLog.map(entry => (
            <div key={entry.id} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: `1px solid ${T.brd}`, fontSize: 12 }}>
              <span style={{ color: T.dim, minWidth: 140 }}>{new Date(entry.created_at).toLocaleString()}</span>
              <Badge c={entry.action === 'issued' ? 'ok' : entry.action === 'suspended' ? 'err' : 'pri'}>{entry.action}</Badge>
              <span style={{ color: T.muted }}>{entry.module}</span>
              <span style={{ color: T.dim, marginLeft: 'auto' }}>by {entry.performed_by}</span>
            </div>
          ))}
        </div>
      )}

      {/* Issue Modal */}
      <Modal open={issueModal} onClose={() => setIssueModal(false)} title="Issue New License">
        <Input label="Module" value={issueForm.module} onChange={v => setIssueForm(p => ({ ...p, module: v }))}
          options={[
            { value: 'base', label: 'Base (Student Management)' },
            { value: 'martial_arts', label: 'Martial Arts / Dojo' },
            { value: 'fitness', label: 'Fitness / Personal Training' },
            { value: 'marketing', label: 'Marketing Suite' },
            { value: 'website', label: 'Website / Public Site' },
          ]} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <Input label="Max Students" value={issueForm.maxStudents} onChange={v => setIssueForm(p => ({ ...p, maxStudents: parseInt(v) || 0 }))} type="number" />
          <Input label="Max Staff" value={issueForm.maxStaff} onChange={v => setIssueForm(p => ({ ...p, maxStaff: parseInt(v) || 0 }))} type="number" />
          <Input label="Monthly Price ($)" value={issueForm.priceMonthly} onChange={v => setIssueForm(p => ({ ...p, priceMonthly: parseFloat(v) || 0 }))} type="number" />
          <Input label="Expires (optional)" value={issueForm.expiresAt || ''} onChange={v => setIssueForm(p => ({ ...p, expiresAt: v || null }))} type="date" />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
          <Btn onClick={() => setIssueModal(false)}>Cancel</Btn>
          <Btn v="pri" onClick={handleIssue}>Issue License</Btn>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)} title="Edit License">
        <Input label="Status" value={editForm.status} onChange={v => setEditForm(p => ({ ...p, status: v }))}
          options={['active', 'trial', 'suspended', 'expired']} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <Input label="Max Students" value={editForm.maxStudents} onChange={v => setEditForm(p => ({ ...p, maxStudents: parseInt(v) || 0 }))} type="number" />
          <Input label="Max Staff" value={editForm.maxStaff} onChange={v => setEditForm(p => ({ ...p, maxStaff: parseInt(v) || 0 }))} type="number" />
          <Input label="Monthly Price ($)" value={editForm.priceMonthly} onChange={v => setEditForm(p => ({ ...p, priceMonthly: parseFloat(v) || 0 }))} type="number" />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
          <Btn onClick={() => setEditModal(null)}>Cancel</Btn>
          <Btn v="pri" onClick={handleUpdate}>Save Changes</Btn>
        </div>
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════════
export default function App() {
  const [user, setUser] = useState(getStoredUser);
  const [stats, setStats] = useState({});
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPartnerOpen, setNewPartnerOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, orgsRes] = await Promise.all([licensesApi.stats(), licensesApi.organizations()]);
      setStats(statsRes.data);
      setOrgs(orgsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
    setLoading(false);
  }, []);

  const loadOrgDetail = useCallback(async (org) => {
    try {
      const { data } = await licensesApi.getOrg(org.id);
      setSelectedOrg(data);
    } catch { setSelectedOrg(org); }
  }, []);

  useEffect(() => {
    if (user) loadData();
    else setLoading(false);
  }, [user, loadData]);

  const handleLogin = (u) => { setUser(u); };
  const handleLogout = () => { clearTokens(); setUser(null); setSelectedOrg(null); };

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div style={{ display: 'flex', height: '100vh', background: T.bg, fontFamily: "'Outfit', -apple-system, sans-serif", color: T.text, overflow: 'hidden' }}>
      {/* Sidebar */}
      <div style={{ width: 240, background: T.card, borderRight: `1px solid ${T.brd}`, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 18, borderBottom: `1px solid ${T.brd}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${T.pri}, ${T.mag})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>P</span>
          </div>
          <div><div style={{ fontSize: 14, fontWeight: 700 }}>Phoenix</div><div style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: .5 }}>License Portal</div></div>
        </div>
        <div style={{ flex: 1, padding: '12px 10px' }}>
          <button onClick={() => setSelectedOrg(null)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', borderRadius: 8, border: 'none', background: !selectedOrg ? T.priD : 'transparent', color: !selectedOrg ? T.pri : T.muted, cursor: 'pointer', fontSize: 13, fontWeight: !selectedOrg ? 600 : 400, textAlign: 'left' }}>
            Dashboard
          </button>
        </div>
        <div style={{ padding: 10, borderTop: `1px solid ${T.brd}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: T.pri, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff' }}>{user.firstName?.[0]}{user.lastName?.[0]}</div>
            <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 500 }}>{user.firstName} {user.lastName}</div><div style={{ fontSize: 10, color: T.dim }}>Super Admin</div></div>
          </div>
          <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: T.muted, cursor: 'pointer', fontSize: 12, textAlign: 'left' }}>Sign Out</button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, overflow: 'auto', padding: 28 }}>
        {loading ? <div style={{ textAlign: 'center', padding: 60, color: T.dim }}>Loading...</div>
          : selectedOrg
            ? <OrgDetailView org={selectedOrg} onBack={() => setSelectedOrg(null)} onRefresh={async () => { await loadData(); await loadOrgDetail(selectedOrg); }} />
            : <DashboardView stats={stats} orgs={orgs} onSelectOrg={(org) => loadOrgDetail(org)} onNewPartnerOrg={() => setNewPartnerOpen(true)} />
        }
      </div>
      {newPartnerOpen && (
        <NewPartnerOrgModal
          onClose={() => setNewPartnerOpen(false)}
          onCreated={async (result) => {
            setNewPartnerOpen(false);
            await loadData();
            if (result?.orgId) {
              try {
                const { data } = await licensesApi.getOrg(result.orgId);
                setSelectedOrg(data);
              } catch {}
            }
          }}
        />
      )}
    </div>
  );
}

// ─── New partner org modal ─────────────────────────────────────────────────
// Super-admin-only "create org from scratch" flow. Skips the public
// register/pay-for-trial flow entirely — used for business partners,
// beta customers, and internal tenants.

const MODULES = [
  { key: 'base', label: 'Base (required)', always: true },
  { key: 'martial_arts', label: 'Martial Arts' },
  { key: 'fitness', label: 'Fitness / PT' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'website', label: 'Website' },
];

const PARTNER_TYPES = [
  { value: 'reseller', label: 'Reseller' },
  { value: 'integrator', label: 'Integrator' },
  { value: 'beta', label: 'Beta customer' },
  { value: 'internal', label: 'Internal / test' },
  { value: 'friends_family', label: 'Friends & family' },
];

function NewPartnerOrgModal({ onClose, onCreated }) {
  const [form, setForm] = React.useState({
    orgName: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    partnerType: 'beta',
    partnerNotes: '',
    modules: ['base', 'martial_arts', 'fitness', 'marketing', 'website'],
    maxStudents: 9999,
    maxStaff: 100,
    priceMonthly: 0,
    sendWelcomeEmail: true,
  });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [result, setResult] = React.useState(null);

  const toggleModule = (key) => {
    setForm((f) => {
      const has = f.modules.includes(key);
      const next = has ? f.modules.filter((m) => m !== key) : [...f.modules, key];
      // Base is required
      if (!next.includes('base')) next.unshift('base');
      return { ...f, modules: next };
    });
  };

  const submit = async () => {
    if (!form.orgName || !form.adminEmail || !form.adminFirstName || !form.adminLastName) {
      setErr('Org name, admin first name, last name, and email are required');
      return;
    }
    setBusy(true); setErr(null);
    try {
      const { data } = await licensesApi.createOrg(form);
      setResult(data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div onClick={onClose} style={modalBackdrop}>
        <div onClick={(e) => e.stopPropagation()} style={{ ...modalBox, maxWidth: 480 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 10px' }}>Partner org created ✓</h3>
          <p style={{ color: T.muted, fontSize: 13, marginBottom: 14 }}>
            {result.licensesIssued} licenses issued.
            {result.welcomeEmailSent && ' Welcome email sent with a password-reset link.'}
          </p>
          {result.tempPassword && (
            <div style={{ padding: 12, background: T.warnD, border: `1px solid ${T.warn}33`, borderRadius: 8, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.warn, marginBottom: 6 }}>Temporary password — share out of band</div>
              <code style={{ fontSize: 13, color: T.text, fontFamily: 'monospace', letterSpacing: 0.5 }}>{result.tempPassword}</code>
            </div>
          )}
          <Btn v="pri" full onClick={() => onCreated(result)}>View the new org</Btn>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalBox, maxWidth: 560 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 4px' }}>Create partner org</h3>
        <p style={{ color: T.muted, fontSize: 12, marginBottom: 16 }}>
          Bypasses the public signup flow. No Stripe. Licenses issued for free.
        </p>

        <Input label="Organisation name" value={form.orgName} onChange={(v) => setForm((f) => ({ ...f, orgName: v }))} placeholder="Iron Dragon Dojo" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="Admin first name" value={form.adminFirstName} onChange={(v) => setForm((f) => ({ ...f, adminFirstName: v }))} />
          <Input label="Admin last name" value={form.adminLastName} onChange={(v) => setForm((f) => ({ ...f, adminLastName: v }))} />
        </div>
        <Input label="Admin email" type="email" value={form.adminEmail} onChange={(v) => setForm((f) => ({ ...f, adminEmail: v }))} placeholder="admin@partnerstudio.com.au" />
        <Input label="Partner type" value={form.partnerType} onChange={(v) => setForm((f) => ({ ...f, partnerType: v }))} options={PARTNER_TYPES} />
        <Input label="Notes" value={form.partnerNotes} onChange={(v) => setForm((f) => ({ ...f, partnerNotes: v }))} placeholder="How did they come on board? Any custom arrangements?" />

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: T.muted, marginBottom: 6 }}>Modules</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {MODULES.map((m) => {
              const on = form.modules.includes(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => !m.always && toggleModule(m.key)}
                  disabled={m.always}
                  style={{
                    padding: '8px 12px', borderRadius: 7, fontSize: 12, cursor: m.always ? 'default' : 'pointer',
                    border: `1px solid ${on ? T.pri : T.brd}`,
                    background: on ? T.priD : T.el,
                    color: on ? T.pri : T.muted,
                    fontWeight: on ? 600 : 400,
                  }}
                >
                  {on && '✓ '}{m.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Input label="Max students" type="number" value={String(form.maxStudents)} onChange={(v) => setForm((f) => ({ ...f, maxStudents: parseInt(v || '0') }))} />
          <Input label="Max staff" type="number" value={String(form.maxStaff)} onChange={(v) => setForm((f) => ({ ...f, maxStaff: parseInt(v || '0') }))} />
          <Input label="Price/month ($)" type="number" value={String(form.priceMonthly)} onChange={(v) => setForm((f) => ({ ...f, priceMonthly: parseFloat(v || '0') }))} />
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, margin: '10px 0 14px', color: T.text }}>
          <input type="checkbox" checked={form.sendWelcomeEmail} onChange={(e) => setForm((f) => ({ ...f, sendWelcomeEmail: e.target.checked }))} />
          Send welcome email with password-reset link
          <span style={{ fontSize: 11, color: T.muted }}>(otherwise a temp password is returned to you)</span>
        </label>

        {err && <div style={{ padding: 10, background: T.errD, color: T.err, borderRadius: 7, fontSize: 12, marginBottom: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn v="pri" onClick={submit} disabled={busy}>{busy ? 'Creating...' : 'Create org'}</Btn>
        </div>
      </div>
    </div>
  );
}

const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20, zIndex: 1000,
};
const modalBox = {
  background: T.card, border: `1px solid ${T.brd}`, borderRadius: 14,
  padding: 24, width: '100%', maxHeight: '90vh', overflowY: 'auto',
};
